<template>
    <!-- Div to help align form -->
    <div class="align-center-helper">
        <infobox-generic
        v-if="modeType === 'upload'"
        state="warning"
        :label="t('cards.batchfile.newCharge.step3.alertLabel')"
        :content="t('cards.batchfile.newCharge.step3.alertContent')"/>
        <!-- *********************************************
        *              Origin Account Block              *
        ********************************************** -->
        <divider-generic
        icon="account-balance"
        :title="t('cards.batchfile.newCharge.step3.originAccountTitle')"
        :applyBorder="false"/>
        <!-- Account Dropdown -->
        <text-block-generic
        :mask="ACCOUNT_NUMBER"
        :params="{
            label: t('cards.batchfile.newCharge.step3.account'),
            content: getAccounts.find(obj => obj.id === formData.originAccount).accountNumber
        }"/>
        <!-- *********************************************
        *                Information Block               *
        ********************************************** -->
        <divider-generic icon="info" :title="t('cards.batchfile.newCharge.step3.infoTitle')"/>
        <!-- File Description -->
        <text-block-generic
        :params="{
            label: t('cards.batchfile.newCharge.step3.fileDescription'),
            content: formData.fileDescription
        }"/>
        <!-- Email -->
        <text-block-generic
        :params="{
            label: t('cards.batchfile.newCharge.step3.email'),
            content: formData.email
        }"/>
        <!-- Available Plafond -->
        <text-block-generic
        v-if="getCardGroupPlafond && getCardGroupPlafond !== -1"
        :params="{
            label: t('cards.batchfile.newCharge.step3.plafond'),
            content: amountFormat(getCardGroupPlafond, ACCOUNT_MAIN_CURRENCY)
        }"/>
        <!-- *********************************************
        *                Charge List Block               *
        ********************************************** -->
        <divider-generic icon="card-recharge" :title="t('cards.batchfile.newCharge.step3.chargeListTitle')"/>
    </div>
    <!-- Table -->
    <table-generic
    :hasDetail="false"
    :headerData="tableHeaderData"
    :tableStyles="tableStyles"
    :tableData="tableData"/>
</template>

<script setup>
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import TableGeneric from '@/components/generic/table/Generic'

/* Vue */
import { computed } from 'vue'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { amountFormat } from '@/assets/scripts/amounts'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@formData {Object} Object with info for our current form
@batchfileEntries {Array} Array of charge entries
@mandatoryList { Array } Array of mandatory keys corresponding with our form
@showMandatoryErrors { Boolean } Prop to trigger display of mandatory errors
@clearFormErrors { Boolean } - Prop to remove of form errors
*/
const props = defineProps({
    modeType: String,
	formData: Object,
    batchfileEntries: Array
})

const { getCards, getAccounts, getCardGroupPlafond } = mapGetters(moduleMapper.CARDS_BATCHFILE_NEW_CHARGE)

const tableHeaderData = [
	{ label: t('cards.batchfile.newCharge.step2.card'), dataAttr: 'cardId' },
    { label: t('cards.batchfile.newCharge.step2.amount'), dataAttr: 'amount' },
    { label: t('cards.batchfile.newCharge.step2.description'), dataAttr: 'description' }
]

const tableStyles = [
    { width: '160px', 'min-width': '160px' },
    { 'text-align': 'end' }
]

const currency = computed(() => {
    return getAccounts.value.find(obj => obj.id === props.formData.originAccount).currency
})

const tableData = computed(() => {
    return props.batchfileEntries.map((entry, index) => {
        return [
            { value: index, dataAttr: 'id' },
            {
                type: 'title',
                value: getCards.value.find(obj => obj.id === entry.cardId.value).cardNumber,
                dataAttr: 'cardId'
            },
            {
                type: 'amount',
                value: {
                    content: entry.amount.value,
                    currency: currency.value,
                    noBalanceIcon: true
                },
                dataAttr: 'amount'
            },
            {
                value: entry.description.value.length ? entry.description.value : '-',
                dataAttr: 'description'
            }
        ]
    })
})
</script>

<style lang="scss" scoped>
.align-center-helper {
    width: 508px;
    max-width: 100%;

    display: flex;
    flex-direction: column;
    gap: 24px;
}
</style>
